import React from 'react';
import { motion } from 'framer-motion';
import { FiBarChart2, FiX } from 'react-icons/fi';
import { useGame } from '../../contexts/GameContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { TYPE_EFFECTS } from '../../constants/specialFish';
import ContentDate from '../common/ContentDate';
import SafeIcon from '../common/SafeIcon';

const QUESTION_TITLES = {
  'zh-TW': '如何查看各祈福類型的敲擊統計？',
  'en-US': 'How can you view tap statistics by blessing type?',
  'ja-JP': '祈福タイプ別のタップ統計を確認する方法は？',
};

const TypeStatsModal = ({ onClose }) => {
  const { state } = useGame();
  const { t, language } = useLanguage();
  const questionTitle = (
    QUESTION_TITLES[language] || QUESTION_TITLES['zh-TW']
  );

  const getTypeStats = () => {
    return Object.entries(TYPE_EFFECTS).map(([id, config]) => ({
      id,
      name: t(`types.${id}.name`) || config.name,
      icon: config.icon,
      color: config.color,
      count: state.typeStats[id]?.tapCount || 0,
    }));
  };

  const getTotalTypeTaps = () => {
    return Object.values(state.typeStats).reduce(
      (sum, stat) => sum + (stat.tapCount || 0),
      0,
    );
  };

  const typeStats = getTypeStats();
  const totalTaps = getTotalTypeTaps();

  return (
    <article
      className="type-stats-modal-content"
      aria-labelledby="type-stats-heading"
      itemScope
      itemType="https://schema.org/Article"
    >
      <header className="modal-header-sticky">
        <div>
          <h2
            id="type-stats-heading"
            className="text-2xl font-bold text-amber-800 flex items-center gap-2"
            itemProp="headline"
          >
            <SafeIcon icon={FiBarChart2} />
            {questionTitle}
          </h2>
          <p
            className="text-sm text-gray-600 mt-1"
            itemProp="description"
          >
            {t('typeStats.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label={t('common.close')}
        >
          <SafeIcon icon={FiX} className="text-xl" />
        </button>
      </header>

      <ContentDate />

      <motion.section
        className="mb-6 relative overflow-hidden rounded-2xl"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        aria-label={t('typeStats.totalTaps')}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 opacity-90" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1zbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxIiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
        <div className="relative p-8 text-center text-white">
          <div className="text-sm font-semibold mb-2 opacity-90">
            {t('typeStats.totalTaps')}
          </div>
          <motion.div
            className="text-6xl font-black mb-2"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            {totalTaps.toLocaleString()}
          </motion.div>
          <div className="text-sm opacity-80">
            {t('typeStats.allTypes')}
          </div>
        </div>
      </motion.section>

      <section
        className="grid grid-cols-2 gap-4 mb-6"
        aria-label={questionTitle}
        itemProp="articleBody"
      >
        {typeStats.map((type, index) => {
          const percentage = totalTaps > 0
            ? Math.round((type.count / totalTaps) * 100)
            : 0;
          const circumference = 2 * Math.PI * 45;
          const offset = circumference
            - (percentage / 100) * circumference;

          return (
            <motion.div
              key={type.id}
              className="relative bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 hover:border-gray-200 transition-all"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex flex-col items-center mb-4">
                <div className="relative w-28 h-28">
                  <svg
                    className="w-full h-full -rotate-90"
                    aria-hidden="true"
                  >
                    <circle
                      cx="56"
                      cy="56"
                      r="45"
                      fill="none"
                      stroke="#f3f4f6"
                      strokeWidth="8"
                    />
                    <motion.circle
                      cx="56"
                      cy="56"
                      r="45"
                      fill="none"
                      stroke={type.color}
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      initial={{ strokeDashoffset: circumference }}
                      animate={{ strokeDashoffset: offset }}
                      transition={{
                        duration: 1,
                        ease: 'easeOut',
                        delay: index * 0.1,
                      }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl mb-1" aria-hidden="true">
                      {type.icon}
                    </span>
                    <span
                      className="text-xl font-black"
                      style={{ color: type.color }}
                    >
                      {percentage}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <h3 className="font-bold text-gray-800 mb-1">
                  {type.name}
                </h3>
                <div
                  className="text-2xl font-black mb-1"
                  style={{ color: type.color }}
                >
                  {type.count.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">
                  {t('typeStats.times')}
                </div>
              </div>

              <div
                className="absolute top-0 right-0 w-20 h-20 rounded-full opacity-10 blur-2xl"
                style={{ background: type.color }}
                aria-hidden="true"
              />
            </motion.div>
          );
        })}
      </section>

      <section className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border-2 border-gray-200">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span aria-hidden="true">🏆</span>
          <span>哪種祈福類型的敲擊次數最多？</span>
        </h3>
        <div className="space-y-3">
          {[...typeStats]
            .sort((firstType, secondType) => (
              secondType.count - firstType.count
            ))
            .map((type, index) => (
              <motion.div
                key={type.id}
                className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center font-black text-sm"
                  style={{
                    background: index === 0
                      ? 'linear-gradient(135deg,#ffd700,#ffed4e)'
                      : index === 1
                        ? 'linear-gradient(135deg,#c0c0c0,#e8e8e8)'
                        : index === 2
                          ? 'linear-gradient(135deg,#cd7f32,#e8a87c)'
                          : '#f3f4f6',
                    color: index < 3 ? '#fff' : '#6b7280',
                  }}
                >
                  {index + 1}
                </div>
                <span className="text-2xl" aria-hidden="true">
                  {type.icon}
                </span>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-bold text-sm text-gray-800">
                      {type.name}
                    </span>
                    <span
                      className="font-black text-sm"
                      style={{ color: type.color }}
                    >
                      {type.count.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <motion.div
                      className="h-1.5 rounded-full"
                      style={{ background: type.color }}
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          totalTaps > 0
                            ? (type.count / totalTaps) * 100
                            : 0
                        }%`,
                      }}
                      transition={{
                        duration: 0.8,
                        delay: index * 0.05,
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
        </div>
      </section>

      <section className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
          <span aria-hidden="true">💡</span>
          <span>{t('typeStats.tips.title')}</span>
        </h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• {t('typeStats.tips.tip1')}</li>
          <li>• {t('typeStats.tips.tip2')}</li>
          <li>• {t('typeStats.tips.tip3')}</li>
        </ul>
      </section>
    </article>
  );
};

TypeStatsModal.managesContentDate = true;

export default TypeStatsModal;